import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";
import { ChevronDown } from "lucide-react";
import { SessionContext } from "../../../../contexts";
import {
  getClinicNotesFeedForCalls,
  type TNotesFeedItem,
} from "../../../../services/api-communications";
import { formatDate, formatTime } from "../../../../utils/timeUtils";
import PrimaryButton from "../../../common/native/PrimaryButton";
import type { Patient as VoiceNotesPatient } from "./voice-notes/types/VoiceNotes";
import VoiceNotesList from "./voice-notes/VoiceNotesList";

type NotesViewProps = {
  useDummyData: boolean;
};

type NotesDisplayMode = "groupedByPatient" | "chronological";

type NoteTableRow = {
  noteId: string;
  createdAt: string;
  patientUuid: string;
  patientName: string;
  encounterType: string;
  noteType: string;
  patient: VoiceNotesPatient;
};

type SelectedNote = {
  noteId: string;
  patient: VoiceNotesPatient;
  patientName: string;
  createdAt: string;
};

type PatientGroup = {
  patientUuid: string;
  patientName: string;
  rows: NoteTableRow[];
  latestCreatedAt: string;
};

const NOTES_DISPLAY_MODE_OPTIONS: Array<{
  label: string;
  value: NotesDisplayMode;
}> = [
  { label: "Grouped by Patient", value: "groupedByPatient" },
  { label: "Chronological", value: "chronological" },
];
const NOTES_FEED_PAGE_LIMIT = 100;

const formatPatientName = (firstName?: string, lastName?: string): string => {
  const fullName = `${firstName || ""} ${lastName || ""}`.trim();
  return fullName || "Unknown Patient";
};

const formatDisplayDateTime = (value: string): string => {
  const dateLabel = formatDate(value);
  if (!dateLabel) {
    return value;
  }
  const timeLabel = formatTime(value);
  return timeLabel ? `${dateLabel} ${timeLabel}` : dateLabel;
};

const formatNoteType = (value: string): string => {
  if (!value || !value.trim()) {
    return "Unknown";
  }

  return value
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const toSortableTimestamp = (value: string): number => {
  const utcValue = value.endsWith("Z") ? value : `${value}Z`;
  const timestamp = Date.parse(utcValue);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const normalizeEncounterType = (item: TNotesFeedItem): string => {
  const encounterTypeRaw =
    item.encounter_type ?? item.audio_info?.[0]?.encounter_type ?? null;

  if (typeof encounterTypeRaw === "string" && encounterTypeRaw.trim()) {
    return encounterTypeRaw.trim();
  }

  return "Consultation";
};

const mapToTableRow = (item: TNotesFeedItem): NoteTableRow | null => {
  if (!item.patient_uuid || item.note_type === "patient_summary") {
    return null;
  }

  const patientUuid = item.patient_uuid;
  const patientName =
    item.patient_name?.display_name?.trim() ||
    formatPatientName(
      item.patient_name?.first_name ?? undefined,
      item.patient_name?.last_name ?? undefined,
    );

  const patientForVoiceNotes: VoiceNotesPatient = {
    id: patientUuid,
    clinicOSUUID: patientUuid,
    consultationDate: "",
    name: patientName,
  };

  return {
    noteId: String(item.note_id),
    createdAt: item.created_at,
    patientUuid,
    patientName,
    encounterType: normalizeEncounterType(item),
    noteType: formatNoteType(item.note_type || ""),
    patient: patientForVoiceNotes,
  };
};

const ConsultNotesView: React.FC<NotesViewProps> = ({
  useDummyData: _useDummyData,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const session = useContext(SessionContext)?.session;
  const modeSelectorButtonRef = useRef<TouchableOpacity | null>(null);
  const loadRequestIdRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [noteRows, setNoteRows] = useState<NoteTableRow[]>([]);
  const [selectedNote, setSelectedNote] = useState<SelectedNote | null>(null);
  const [mode, setMode] = useState<NotesDisplayMode>("groupedByPatient");
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [modeMenuAnchor, setModeMenuAnchor] = useState({
    top: 0,
    left: 0,
    width: 220,
  });

  const notesFeedMode = useMemo(
    () =>
      mode === "groupedByPatient" ? "grouped_by_patient" : "chronological",
    [mode],
  );

  const fetchRows = React.useCallback(
    async ({ cursor, append }: { cursor?: string; append: boolean }) => {
      if (!session?.clinicId) {
        setNoteRows([]);
        setNextCursor(null);
        return;
      }

      const requestId = loadRequestIdRef.current + 1;
      loadRequestIdRef.current = requestId;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await getClinicNotesFeedForCalls(session, {
          mode: notesFeedMode,
          limit: NOTES_FEED_PAGE_LIMIT,
          cursor,
          includeSignedPdfUrl: false,
        });

        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        const nextRows = response.items
          .map(mapToTableRow)
          .filter((row): row is NoteTableRow => Boolean(row));

        setNoteRows((prevRows) => {
          const merged = append ? [...prevRows, ...nextRows] : nextRows;
          const dedupedById = new Map<string, NoteTableRow>();
          merged.forEach((row) => {
            dedupedById.set(row.noteId, row);
          });
          const dedupedRows = Array.from(dedupedById.values());

          if (notesFeedMode === "chronological") {
            dedupedRows.sort(
              (left, right) =>
                toSortableTimestamp(right.createdAt) -
                toSortableTimestamp(left.createdAt),
            );
          }

          return dedupedRows;
        });
        setNextCursor(response.next_cursor || null);
        setError(null);
      } catch (fetchError) {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : "Failed to load notes.",
        );
      } finally {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [notesFeedMode, session],
  );

  const refreshRows = React.useCallback(() => {
    if (!session?.clinicId) {
      setNoteRows([]);
      setNextCursor(null);
      return;
    }

    setSelectedNote(null);
    setNextCursor(null);
    setNoteRows([]);
    fetchRows({ append: false });
  }, [fetchRows, session?.clinicId]);

  useEffect(() => {
    refreshRows();
  }, [refreshRows]);

  const groupedNotes = useMemo(() => {
    const grouped = new Map<string, PatientGroup>();

    noteRows.forEach((row) => {
      const existing = grouped.get(row.patientUuid);
      if (!existing) {
        grouped.set(row.patientUuid, {
          patientUuid: row.patientUuid,
          patientName: row.patientName,
          rows: [row],
          latestCreatedAt: row.createdAt,
        });
        return;
      }

      existing.rows.push(row);
      if (
        toSortableTimestamp(row.createdAt) >
        toSortableTimestamp(existing.latestCreatedAt)
      ) {
        existing.latestCreatedAt = row.createdAt;
      }
    });

    grouped.forEach((group) => {
      group.rows.sort(
        (left, right) =>
          toSortableTimestamp(right.createdAt) -
          toSortableTimestamp(left.createdAt),
      );
    });

    return Array.from(grouped.values()).sort(
      (left, right) =>
        toSortableTimestamp(right.latestCreatedAt) -
        toSortableTimestamp(left.latestCreatedAt),
    );
  }, [noteRows]);

  const selectedModeLabel = useMemo(
    () =>
      NOTES_DISPLAY_MODE_OPTIONS.find((option) => option.value === mode)
        ?.label ?? "Grouped by Patient",
    [mode],
  );

  const handleOpenModeMenu = () => {
    if (!modeSelectorButtonRef.current) {
      setShowModeMenu(true);
      return;
    }

    modeSelectorButtonRef.current.measureInWindow((x, y, width, height) => {
      const menuWidth = Math.max(220, width);
      const clampedLeft = Math.max(
        12,
        Math.min(x, windowWidth - menuWidth - 12),
      );
      setModeMenuAnchor({
        top: y + height + 8,
        left: clampedLeft,
        width: menuWidth,
      });
      setShowModeMenu(true);
    });
  };

  const renderNotesTable = (rows: NoteTableRow[]) => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View>
        <View style={[styles.tableRow, styles.tableHeaderRow]}>
          <Text
            style={[styles.tableCell, styles.headerCell, styles.dateColumn]}
          >
            Date/Time Created
          </Text>
          <Text
            style={[styles.tableCell, styles.headerCell, styles.patientColumn]}
          >
            Patient
          </Text>
          <Text
            style={[
              styles.tableCell,
              styles.headerCell,
              styles.encounterColumn,
            ]}
          >
            Encounter Type
          </Text>
          <Text
            style={[styles.tableCell, styles.headerCell, styles.noteTypeColumn]}
          >
            Note Type
          </Text>
          <Text
            style={[styles.tableCell, styles.headerCell, styles.openColumn]}
          >
            Open
          </Text>
        </View>

        {rows.map((row) => (
          <View
            key={`${row.patientUuid}-${row.noteId}`}
            style={styles.tableRow}
          >
            <Text style={[styles.tableCell, styles.dateColumn]}>
              {formatDisplayDateTime(row.createdAt)}
            </Text>
            <Text style={[styles.tableCell, styles.patientColumn]}>
              {row.patientName}
            </Text>
            <Text style={[styles.tableCell, styles.encounterColumn]}>
              {row.encounterType}
            </Text>
            <Text style={[styles.tableCell, styles.noteTypeColumn]}>
              {row.noteType}
            </Text>
            <View style={[styles.tableCell, styles.openColumn]}>
              <TouchableOpacity
                style={styles.openLinkButton}
                onPress={() =>
                  setSelectedNote({
                    noteId: row.noteId,
                    patient: row.patient,
                    patientName: row.patientName,
                    createdAt: row.createdAt,
                  })
                }
              >
                <Text style={styles.openLinkText}>View Note</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </View>
    </ScrollView>
  );

  return (
    <View style={styles.container}>
      <View style={styles.modeSelectorContainer}>
        <TouchableOpacity
          ref={modeSelectorButtonRef}
          style={styles.modeSelectorButton}
          onPress={handleOpenModeMenu}
          accessibilityRole="button"
          accessibilityLabel="Select notes mode"
        >
          <Text style={styles.modeSelectorButtonText}>{selectedModeLabel}</Text>
          <ChevronDown size={16} color="#1F2937" />
        </TouchableOpacity>
      </View>

      <Modal
        transparent
        visible={showModeMenu}
        animationType="fade"
        onRequestClose={() => setShowModeMenu(false)}
      >
        <Pressable
          style={styles.modeSelectorModalBackdrop}
          onPress={() => setShowModeMenu(false)}
        >
          <View
            style={[
              styles.modeSelectorMenu,
              {
                top: modeMenuAnchor.top,
                left: modeMenuAnchor.left,
                width: modeMenuAnchor.width,
              },
            ]}
          >
            {NOTES_DISPLAY_MODE_OPTIONS.map((option) => {
              const isSelected = option.value === mode;
              return (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.modeSelectorMenuItem,
                    isSelected && styles.modeSelectorMenuItemSelected,
                  ]}
                  onPress={() => {
                    setMode(option.value);
                    setShowModeMenu(false);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`Switch notes mode to ${option.label}`}
                >
                  <Text
                    style={[
                      styles.modeSelectorMenuItemText,
                      isSelected && styles.modeSelectorMenuItemTextSelected,
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </Pressable>
      </Modal>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#0085FF" />
          <Text style={styles.loadingText}>Loading notes...</Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
          <PrimaryButton text="Retry" onPress={refreshRows} width={140} />
        </View>
      ) : groupedNotes.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No notes found.</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
        >
          {mode === "groupedByPatient"
            ? groupedNotes.map((group) => (
                <View key={group.patientUuid} style={styles.groupCard}>
                  <Text style={styles.groupTitle}>{group.patientName}</Text>
                  {renderNotesTable(group.rows)}
                </View>
              ))
            : renderNotesTable(noteRows)}

          {nextCursor ? (
            <View style={styles.loadMoreContainer}>
              <PrimaryButton
                text="Load more notes"
                onPress={() => fetchRows({ append: true, cursor: nextCursor })}
                disabled={loadingMore}
                loading={loadingMore}
              />
            </View>
          ) : null}
        </ScrollView>
      )}

      <Modal
        transparent
        visible={Boolean(selectedNote)}
        animationType="fade"
        onRequestClose={() => setSelectedNote(null)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setSelectedNote(null)}
        >
          <Pressable style={styles.modalContent} onPress={() => undefined}>
            {selectedNote ? (
              <>
                <View style={styles.modalHeader}>
                  <View style={styles.modalTitleContainer}>
                    <Text style={styles.modalTitle}>Note</Text>
                    <Text style={styles.modalSubtitle}>
                      {selectedNote.patientName} •{" "}
                      {formatDisplayDateTime(selectedNote.createdAt)}
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={styles.modalCloseButton}
                    onPress={() => setSelectedNote(null)}
                  >
                    <Text style={styles.modalCloseButtonText}>Close</Text>
                  </TouchableOpacity>
                </View>

                <ScrollView
                  style={styles.modalBody}
                  contentContainerStyle={styles.modalBodyContent}
                >
                  <VoiceNotesList
                    patient={selectedNote.patient}
                    refreshKey={0}
                    tipsEnabled={false}
                    filteredNoteId={selectedNote.noteId}
                    hideUnprocessedAudios
                  />
                </ScrollView>
              </>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  modeSelectorContainer: {
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 8,
  },
  modeSelectorButton: {
    minHeight: 36,
    maxWidth: 220,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
  },
  modeSelectorButtonText: {
    fontFamily: "Urbanist500",
    fontSize: 14,
    lineHeight: 20,
    color: "#1F2937",
    marginRight: 8,
  },
  modeSelectorModalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.08)",
  },
  modeSelectorMenu: {
    position: "absolute",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 6,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 20,
    elevation: 24,
  },
  modeSelectorMenuItem: {
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: 12,
    borderRadius: 8,
    marginHorizontal: 6,
  },
  modeSelectorMenuItemSelected: {
    backgroundColor: "#EAF7FF",
  },
  modeSelectorMenuItemText: {
    fontFamily: "Urbanist500",
    fontSize: 14,
    lineHeight: 20,
    color: "#111827",
  },
  modeSelectorMenuItemTextSelected: {
    color: "#0A6FD6",
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    padding: 12,
    paddingBottom: 24,
    rowGap: 12,
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 20,
  },
  loadingText: {
    marginLeft: 8,
    color: "#4B5563",
    fontSize: 14,
  },
  errorContainer: {
    margin: 12,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#FFF2F0",
    alignItems: "center",
    gap: 10,
  },
  errorText: {
    color: "#E53935",
    fontSize: 14,
  },
  emptyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  emptyText: {
    color: "#6B7280",
    fontSize: 14,
  },
  groupCard: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: 12,
  },
  groupTitle: {
    color: "#111827",
    fontSize: 18,
    fontFamily: "Urbanist600",
    marginBottom: 10,
  },
  loadMoreContainer: {
    alignItems: "flex-start",
    marginTop: 4,
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#F0F2F5",
    minHeight: 46,
  },
  tableHeaderRow: {
    backgroundColor: "#F8FAFC",
    borderBottomColor: "#E5E7EB",
  },
  tableCell: {
    paddingHorizontal: 8,
    paddingVertical: 10,
    color: "#111827",
    fontSize: 13,
  },
  headerCell: {
    fontFamily: "Urbanist600",
    color: "#374151",
    fontSize: 12,
    textTransform: "uppercase",
  },
  dateColumn: {
    width: 190,
  },
  patientColumn: {
    width: 170,
  },
  encounterColumn: {
    width: 170,
  },
  noteTypeColumn: {
    width: 170,
  },
  openColumn: {
    width: 100,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  openLinkButton: {
    minHeight: 32,
    justifyContent: "center",
  },
  openLinkText: {
    color: "#0085FF",
    fontFamily: "Urbanist600",
    fontSize: 13,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalContent: {
    width: "100%",
    maxWidth: 980,
    maxHeight: "90%",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  modalTitleContainer: {
    flex: 1,
    minWidth: 0,
    marginRight: 12,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: "Urbanist600",
    color: "#111827",
  },
  modalSubtitle: {
    marginTop: 2,
    fontSize: 12,
    color: "#6B7280",
  },
  modalCloseButton: {
    minHeight: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    paddingHorizontal: 12,
    justifyContent: "center",
  },
  modalCloseButtonText: {
    color: "#374151",
    fontSize: 13,
    fontFamily: "Urbanist500",
  },
  modalBody: {
    flex: 1,
  },
  modalBodyContent: {
    padding: 12,
  },
});

export default ConsultNotesView;
